import React from "react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";

const kebab = (s) => s.replace(/_/g, "-");

const inputClass =
  "h-12 bg-white border border-[#E5E5E0] rounded-lg text-[15px] text-[#1A1A1A] placeholder:text-[#a3a3a3] focus-visible:ring-2 focus-visible:ring-[#010082] focus-visible:ring-offset-0 focus-visible:border-[#010082]";

export const FieldRenderer = ({ field, value, onChange, error }) => {
  const { key, label, type, options = [], placeholder, required } = field;
  const testid = `field-${kebab(key)}`;

  const labelEl = (
    <Label
      htmlFor={testid}
      className="text-[13px] font-medium text-[#1A1A1A] mb-2 flex items-center gap-1"
      data-testid={`label-${kebab(key)}`}
    >
      {label}
      {required && <span className="text-[#010082]">*</span>}
    </Label>
  );

  const errEl = error ? (
    <p className="text-xs text-red-600 mt-1.5" data-testid={`error-${kebab(key)}`}>
      {error}
    </p>
  ) : null;

  if (type === "text" || type === "email" || type === "number") {
    return (
      <div className="w-full">
        {labelEl}
        <Input
          id={testid}
          data-testid={testid}
          type={type}
          value={value || ""}
          placeholder={placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
          inputMode={type === "number" ? "numeric" : undefined}
        />
        {errEl}
      </div>
    );
  }

  if (type === "currency") {
    return (
      <div className="w-full">
        {labelEl}
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373] text-sm">$</span>
          <Input
            id={testid}
            data-testid={testid}
            type="number"
            value={value || ""}
            placeholder="0"
            onChange={(e) => onChange(e.target.value)}
            className={`${inputClass} pl-8`}
          />
        </div>
        {errEl}
      </div>
    );
  }

  if (type === "date") {
    return (
      <div className="w-full">
        {labelEl}
        <Input
          id={testid}
          data-testid={testid}
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
        {errEl}
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div className="w-full">
        {labelEl}
        <Textarea
          id={testid}
          data-testid={testid}
          value={value || ""}
          placeholder={placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          className="min-h-[120px] bg-white border border-[#E5E5E0] rounded-lg text-[15px] text-[#1A1A1A] placeholder:text-[#a3a3a3] focus-visible:ring-2 focus-visible:ring-[#010082] focus-visible:ring-offset-0 focus-visible:border-[#010082]"
        />
        {errEl}
      </div>
    );
  }

  if (type === "radio") {
    return (
      <div className="w-full">
        {labelEl}
        <RadioGroup
          value={value || ""}
          onValueChange={(v) => onChange(v)}
          className="grid grid-cols-1 sm:grid-cols-2 gap-2"
          data-testid={testid}
        >
          {options.map((opt) => {
            const id = `${testid}-${kebab(opt.toLowerCase().replace(/\s+/g, "-"))}`;
            const active = value === opt;
            return (
              <label
                key={opt}
                htmlFor={id}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                  active
                    ? "border-[#010082] bg-[#010082]/[0.04]"
                    : "border-[#E5E5E0] bg-white hover:bg-[#FAFAF8]"
                }`}
                data-testid={`option-${kebab(key)}-${kebab(opt.toLowerCase().replace(/\s+/g, "-"))}`}
              >
                <RadioGroupItem
                  id={id}
                  value={opt}
                  className="border-[#c9c8c3] data-[state=checked]:border-[#010082] data-[state=checked]:text-[#010082]"
                />
                <span className="text-[14px] text-[#1A1A1A]">{opt}</span>
              </label>
            );
          })}
        </RadioGroup>
        {errEl}
      </div>
    );
  }

  if (type === "checkboxes") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      onChange(arr.includes(opt) ? arr.filter((o) => o !== opt) : [...arr, opt]);
    };
    return (
      <div className="w-full">
        {labelEl}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={testid}>
          {options.map((opt) => {
            const id = `${testid}-${kebab(opt.toLowerCase().replace(/\s+/g, "-"))}`;
            const active = arr.includes(opt);
            return (
              <label
                key={opt}
                htmlFor={id}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                  active
                    ? "border-[#010082] bg-[#010082]/[0.04]"
                    : "border-[#E5E5E0] bg-white hover:bg-[#FAFAF8]"
                }`}
                data-testid={`option-${kebab(key)}-${kebab(opt.toLowerCase().replace(/\s+/g, "-"))}`}
              >
                <Checkbox
                  id={id}
                  checked={active}
                  onCheckedChange={() => toggle(opt)}
                  className="border-[#c9c8c3] data-[state=checked]:bg-[#010082] data-[state=checked]:border-[#010082]"
                />
                <span className="text-[14px] text-[#1A1A1A]">{opt}</span>
              </label>
            );
          })}
        </div>
        {errEl}
      </div>
    );
  }

  if (type === "consent") {
    return (
      <div className="w-full">
        <label
          className="flex items-start gap-3 p-5 rounded-xl border border-[#E5E5E0] bg-white cursor-pointer hover:bg-[#FAFAF8] transition-colors"
          data-testid={testid}
        >
          <Checkbox
            checked={!!value}
            onCheckedChange={(v) => onChange(!!v)}
            className="mt-1 border-[#c9c8c3] data-[state=checked]:bg-[#010082] data-[state=checked]:border-[#010082]"
            data-testid={`${testid}-checkbox`}
          />
          <span className="text-[14px] text-[#1A1A1A] leading-relaxed">
            {label} {required && <span className="text-[#010082]">*</span>}
          </span>
        </label>
        {errEl}
      </div>
    );
  }

  return null;
};
